import { useQueries } from "@tanstack/react-query";
import { Text, ProgressCircle, ProgressBar } from "@react-spectrum/s2";
import { style } from "@react-spectrum/s2/style" with { type: "macro" };

import { jobStatusQueryOptions } from "@app/hooks/mogrtTemplates";

const cardStyle = style({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 12,
});

const mediaStyle = style({
  display: "block",
  width: 480,
  aspectRatio: "640 / 360",
  borderRadius: "default",
});

const placeholderStyle = style({
  width: 480,
  aspectRatio: "640 / 360",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "default",
  backgroundColor: "gray-1000/10",
});

const ModularVideoCard = ({ name, videoUrl, isFailed }) => (
  <div className={cardStyle}>
    {videoUrl ? (
      <video src={videoUrl} controls className={mediaStyle} />
    ) : (
      <div className={placeholderStyle}>
        {isFailed ? (
          <Text styles={style({ font: "ui", color: "negative" })}>
            Render failed
          </Text>
        ) : (
          <ProgressCircle
            isIndeterminate
            size="L"
            staticColor="auto"
            aria-label={`Generating ${name}…`}
          />
        )}
      </div>
    )}
    <Text styles={style({ font: "ui", color: "neutral-subdued" })}>{name}</Text>
  </div>
);

export const ModularVideos = ({ jobs = [] }) => {
  const results = useQueries({
    queries: jobs.map((job) => jobStatusQueryOptions(job.jobId)),
  });

  const cards = jobs.map((job, i) => ({
    jobId: job.jobId,
    name: `${job.name} ${i + 1}`,
    videoUrl: results[i]?.data?.outputs?.[0]?.destination?.url,
    isFailed: results[i]?.data?.status === "failed" || results[i]?.isError,
  }));

  const allReady = cards.length > 0 && cards.every((c) => !!c.videoUrl);

  const total = cards.length;
  const doneCount = cards.filter((c) => !!c.videoUrl || c.isFailed).length;
  const failedCount = cards.filter((c) => c.isFailed).length;

  const statusText = allReady
    ? "Merging videos…"
    : doneCount === 0
      ? "Starting video generation…"
      : `Rendering video ${doneCount} of ${total}…`;

  const progressValue = allReady ? total + 1 : doneCount;
  const progressMax = total + 1 + 2;

  return (
    <div
      className={style({
        width: 640,
        marginX: "auto",
        marginTop: -96,
        aspectRatio: "640 / 360",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "default",
        backgroundColor: "gray-1000/10",
      })}
    >
      <ProgressCircle
        isIndeterminate={progressValue === 0}
        value={progressValue}
        maxValue={progressMax}
        size="L"
        staticColor="auto"
        aria-label="Generating video…"
      />
    </div>
  );

  return (
    <div
      className={style({
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 32,
      })}
    >
      <div
        className={style({
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
        })}
      >
        <ProgressBar
          label={statusText}
          value={progressValue}
          maxValue={progressMax}
          styles={style({ width: 320 })}
        />
        {failedCount > 0 && (
          <Text styles={style({ font: "ui", color: "negative" })}>
            {failedCount} video{failedCount > 1 ? "s" : ""} failed to render
          </Text>
        )}
      </div>
      <div
        className={style({
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 32,
        })}
      >
        {cards.map((card) => (
          <ModularVideoCard key={card.jobId} {...card} />
        ))}
      </div>
    </div>
  );
};
